import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import Footer from './Footer';
import LanguageSelector from './LanguageSelector';

const TermsOfService: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-orange-light-gradient">
      {/* Header */}
      <nav className="bg-white shadow-orange border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <Link to={`/${language}`} className="text-xl sm:text-2xl font-bold text-orange-gradient flex-shrink-0 hover:opacity-80 transition-opacity">
              {t.nav.title}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl p-6 sm:p-8 lg:p-12 shadow-orange">
          <h1 className="text-3xl sm:text-4xl font-bold text-orange-gradient mb-8">Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-lg text-gray-600 mb-8">
              <strong>Last updated:</strong> December 2024
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using BG Remover ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Description of Service</h2>
              <p>
                BG Remover is a free, web-based tool that uses artificial intelligence to automatically remove backgrounds from images. The service processes images entirely on your device using client-side technology, ensuring your privacy and data security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Use License</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Permission</h3>
              <p>
                Permission is granted to temporarily use BG Remover for personal and commercial purposes. This is the grant of a license, not a transfer of title, and under this license you may:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Use the service for personal image editing</li>
                <li>Use the service for commercial projects</li>
                <li>Process unlimited images</li>
                <li>Download and use the processed images without restrictions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Restrictions</h3>
              <p>You may not:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Attempt to reverse engineer or copy the service</li>
                <li>Use the service for illegal or harmful purposes</li>
                <li>Upload images that violate copyright or contain illegal content</li>
                <li>Attempt to overload or disrupt the service</li>
                <li>Use automated tools to access the service excessively</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. User Content and Responsibilities</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Your Content</h3>
              <p>
                You retain full ownership of all images you upload to the service. By using our service, you confirm that you have the right to use and process the images you upload.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Content Guidelines</h3>
              <p>You agree not to upload images that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Contain illegal, harmful, or offensive content</li>
                <li>Violate copyright, trademark, or other intellectual property rights</li>
                <li>Contain personal information of others without consent</li>
                <li>Are intended to harass, abuse, or harm others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Privacy and Data Processing</h2>
              <p>
                All image processing occurs locally on your device. We do not store, transmit, or have access to your images. For detailed information about our privacy practices, please review our <Link to={`/${language}/privacy`} className="text-orange-600 hover:text-orange-700 font-medium">Privacy Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Service Availability</h2>
              <p>
                While we strive to maintain high availability, we do not guarantee that the service will be available at all times. The service may be temporarily unavailable due to maintenance, updates, or technical issues. We reserve the right to modify or discontinue the service at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Disclaimer of Warranties</h2>
              <p>
                The service is provided "as is" without any representations or warranties, express or implied. We make no representations or warranties in relation to this service or the information and materials provided on this service.
              </p>
              <p className="mt-4">
                We do not warrant that:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>The service will be constantly available or uninterrupted</li>
                <li>The service will be free of errors or defects</li>
                <li>The results will meet your specific requirements</li>
                <li>Any defects will be corrected</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Limitation of Liability</h2>
              <p>
                In no event shall BG Remover or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the service, even if BG Remover or its authorized representative has been notified orally or in writing of the possibility of such damage.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Accuracy of Materials</h2>
              <p>
                The materials appearing on BG Remover could include technical, typographical, or photographic errors. BG Remover does not warrant that any of the materials on its service are accurate, complete, or current. BG Remover may make changes to the materials contained on its service at any time without notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Modifications</h2>
              <p>
                BG Remover may revise these terms of service at any time without notice. By using this service, you are agreeing to be bound by the then current version of these terms of service. We will update the "Last updated" date at the top of this document when changes are made.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Governing Law</h2>
              <p>
                These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which BG Remover operates, and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mt-4 border border-orange-200">
                <p><strong>Email:</strong> <a href="mailto:contact@waicto.com" className="text-orange-600 hover:text-orange-700">contact@waicto.com</a></p>
                <p><strong>Website:</strong> <a href="https://waicto.com" className="text-orange-600 hover:text-orange-700">waicto.com</a></p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-center text-gray-600">
                <Link to={`/${language}`} className="text-orange-600 hover:text-orange-700 font-medium">
                  ← Back to BG Remover
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;